import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogRef,
  MatDialogActions,
  MatDialogClose,
  MatDialogTitle,
  MatDialogContent,
  MatDialogModule,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

export interface ValidateTimesheetDialogData {
  title: string;
  companyName: string;
  dealName: string;
  message?: string;
  warningMessage?: string;
  confirmText?: string;
  cancelText?: string;
}

export interface ValidateTimesheetDialogResult {
  confirmed: boolean;
}

@Component({
  selector: 'app-validate-timesheet-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatDialogModule,
    MatIconModule
  ],
  template: `
    <div class="dialog-container">
      <h5 mat-dialog-title class="dialog-title">
        <mat-icon class="title-icon">help_outline</mat-icon>
        {{ data.title }}
      </h5>
      
      <div mat-dialog-content class="dialog-content">
        <!-- Info Cards Grid -->
        <div class="info-cards-grid">
          <!-- Company Card -->
          <div class="info-card" style="max-width: 250px;">
            <div class="card-icon company-icon">
              <mat-icon>business</mat-icon>
            </div>
            <div class="card-details">
              <div class="card-label">Company</div>
              <div class="card-value">{{ data.companyName }}</div>
            </div>
          </div>
          
          <!-- Deal Card -->
          <div class="info-card" style="max-width: 250px;">
            <div class="card-icon deal-icon">
              <mat-icon>work</mat-icon>
            </div>
            <div class="card-details">
              <div class="card-label">Deal</div>
              <div class="card-value">{{ data.dealName }}</div>
            </div>
          </div>
        </div>
        
        <!-- Question Box -->
        <div class="question-box">
          <p class="question-text">
            {{ data.message || 'Are you sure you want to validate all the timesheets for this deal ?' }}
          </p>
        </div>
        
        <!-- Warning Box -->
        <div class="warning-box">
          <div class="warning-icon-wrapper">
            <mat-icon class="warning-icon">warning</mat-icon>
          </div>
          <div class="warning-content">
            <p class="warning-title">Warning</p>
            <p class="warning-text">
              {{ data.warningMessage || 'Once validated, it will no longer be possible to add new timesheets to this group.' }}
            </p>
          </div>
        </div>
      </div>
      
      <div mat-dialog-actions class="dialog-actions">
        <button 
          mat-flat-button 
          class="action-btn cancel-btn"
          (click)="onCancel()">
          {{ data.cancelText || 'Cancel' }}
        </button>
        <button 
          mat-flat-button
          class="action-btn confirm-btn"
          (click)="onConfirm()"
          cdkFocusInitial>
          {{ data.confirmText || 'Yes, validate all!' }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      padding: 0;
      min-width: 520px;
      max-width: 600px;
    }

    /* Titre */
    .dialog-title {
      font-size: 18px;
      font-weight: 600;
      color: #2a3547;
      margin: 0;
      padding: 24px 24px 16px 24px;
      line-height: 1.4;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .title-icon {
      color: #5d87ff;
      font-size: 28px;
      width: 28px;
      height: 28px;
    }

    /* Contenu */
    .dialog-content {
      padding: 0 24px 24px 24px !important;
    }

    /* Info Cards Grid */
    .info-cards-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-bottom: 20px;
    }

    .info-card {
      background: white;
      padding: 16px;
      border-radius: 10px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border: 1px solid #e8e9eb;
      display: flex;
      align-items: center;
      gap: 12px;
      transition: all 0.3s ease;
    }

    .info-card:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      transform: translateY(-2px);
    }

    .card-icon {
      width: 44px;
      height: 44px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .company-icon {
      background: linear-gradient(135deg,#635bff 0%,#635bff 100%);
    }

    .deal-icon {
      background: linear-gradient(135deg,#635bff 0%,#635bff 100%);
    }

    .card-icon mat-icon {
      color: white;
      font-size: 22px;
      width: 22px;
      height: 22px;
    }

    .card-details {
      flex: 1;
      min-width: 0;
      overflow: hidden;
    }

    .card-label {
      font-size: 11px;
      color: #9ca3af;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 4px;
      font-weight: 600;
    }

    .card-value {
      color: #2a3547;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.4;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Question Box */
    .question-box {
      background: linear-gradient(135deg, #f6f8fb 0%, #e9ecef 100%);
      padding: 16px;
      border-radius: 10px;
      margin-bottom: 16px;
      border: 1px solid #e0e4e9;
    }

    .question-text {
      color: #2a3547;
      font-size: 15px;
      margin: 0;
      line-height: 1.6;
      font-weight: 500;
    }

    /* Warning Box */
    .warning-box {
      background: linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%);
      border: 2px solid #ffc107;
      border-radius: 10px;
      padding: 16px;
      display: flex;
      align-items: flex-start;
      gap: 12px;
      box-shadow: 0 2px 8px rgba(255, 193, 7, 0.15);
    }

    .warning-icon-wrapper {
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      background: linear-gradient(135deg, #ffc107 0%, #ffb300 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 6px rgba(255, 193, 7, 0.3);
    }

    .warning-icon {
      color: white;
      font-size: 20px;
      width: 20px;
      height: 20px;
      font-weight: bold;
    }

    .warning-content {
      flex: 1;
    }

    .warning-title {
      color: #856404;
      font-size: 14px;
      font-weight: 700;
      margin: 0 0 6px 0;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .warning-text {
      color: #856404;
      font-size: 13px;
      margin: 0;
      line-height: 1.5;
    }

    /* Actions */
    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      padding: 0 24px 24px 24px !important;
      margin: 0 !important;
    }

    .action-btn {
      min-width: 140px;
      padding: 10px 24px;
      font-size: 14px;
      font-weight: 600;
      border-radius: 8px;
      text-transform: none;
      letter-spacing: 0.5px;
      transition: all 0.3s ease;
    }

    .cancel-btn {
      background-color: #ff6692 !important;
      color: white !important;
    }

    .cancel-btn:hover {
      background-color: #ff4d7d !important;
      box-shadow: 0 4px 12px rgba(255, 102, 146, 0.3);
      transform: translateY(-1px);
    }

    .confirm-btn {
      background-color: #635bff !important;
      color: white !important;
    }

    .confirm-btn:hover {
      background-color: #5247e8 !important;
      box-shadow: 0 4px 12px rgba(99, 91, 255, 0.3);
      transform: translateY(-1px);
    }

    /* Responsive */
    @media (max-width: 600px) {
      .dialog-container {
        min-width: 320px;
      }

      .info-cards-grid {
        grid-template-columns: 1fr;
        gap: 12px;
      }

      .dialog-actions {
        flex-direction: column-reverse;
        gap: 10px;
      }

      .action-btn {
        width: 100%;
      }
    }
  `]
})
export class ValidateTimesheetDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ValidateTimesheetDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ValidateTimesheetDialogData
  ) { }

  onConfirm(): void {
    const result: ValidateTimesheetDialogResult = {
      confirmed: true
    };
    this.dialogRef.close(result);
  }

  onCancel(): void {
    const result: ValidateTimesheetDialogResult = {
      confirmed: false
    };
    this.dialogRef.close(result);
  }
}

